import React, { useRef, useState } from 'react'
import classnames from 'classnames'
import { Form } from 'react-bootstrap'
import { Button } from '../Button/Button'
import { humanFileSize } from '../../common/util'
import { useAppSelector } from '../../store/hooks'
import { selectAccountId } from '../../store/auth/authSlice'

import { useLazyDownloadGlobalFileRequestQuery, useUploadFileMutation, useUploadFileRequestMutation } from '../../store/media/file'
import { useUpdateFileMutation } from '../../store/media/media'
import { FileKind, FileStatus } from '../../models/File'
import { Nullable } from '../../common/Nullable'
import { FileInfo } from '../../models/Account'
import { FileDisplayModal } from './FileDisplayModal'
import { ScopedBlock } from '../ScopedBlock/ScopedBlock'
import { MissingScopeAction, ScopeDescriptor } from '../ScopedBlock/models'

export type FileType = 'image' | 'audio'

export type FileHandlerProps = {
  loading: boolean
  enabled: boolean
  fileType: FileType
  fileKind: FileKind
  onFileUploaded: (fileId: string) => void
  onFileReset: () => void
  fileInfo: Nullable<FileInfo>
  maxFileSize: number
  buttonBackgroundImage: string
  supportedMimeTypes: string
  scope: ScopeDescriptor
  defaultFile?: {
    url: string
    text: string
  }
  imageMaxResolution?: {
    maxWidth: number
    maxHeight: number
  }
  imageMinResolution?: {
    minWidth: number
    minHeight: number
  }
}

export const FileHandler: React.FC<FileHandlerProps> = ({
  loading,
  enabled,
  fileType,
  fileKind,
  onFileUploaded,
  fileInfo,
  maxFileSize,
  buttonBackgroundImage,
  supportedMimeTypes,
  onFileReset,
  scope,
  defaultFile,
  imageMaxResolution,
  imageMinResolution,
}) => {
  const accountId = useAppSelector(selectAccountId)
  const [errorMessage, setErrorMessage] = useState('')

  const fileInputRef = useRef<HTMLInputElement>(null)

  const [logoFileUrl, setLogoFileUrl] = useState<string | null>(defaultFile?.url ?? null)
  const [showDisplayFileModal, setShowDisplayFileModal] = useState<boolean>(false)
  const [isUploading, setIsUploading] = useState<boolean>(false)

  const [downloadFileRequest] = useLazyDownloadGlobalFileRequestQuery()
  const [uploadFileRequest] = useUploadFileRequestMutation()
  const [uploadFile] = useUploadFileMutation()
  const [updateFile] = useUpdateFileMutation()

  const isLoading = isUploading || loading
  const buttonText = isLoading ? 'Uploading...' : fileInfo ? 'Change' : 'Upload'
  const text = fileInfo ? fileInfo.fileName : (defaultFile?.text ?? '')

  const handleDisplayFile = async () => {
    if (!fileInfo) {
      setLogoFileUrl(defaultFile?.url ?? null)
      setShowDisplayFileModal(true)
      return
    }
    const {
      data: { location },
    } = await downloadFileRequest({
      accountId,
      fileId: fileInfo.fileId,
    }).unwrap()
    if (location) {
      setLogoFileUrl(location)
      setShowDisplayFileModal(true)
    }
  }

  const removeFile = () => {
    setErrorMessage('')
    if (fileInputRef && fileInputRef.current && fileInputRef.current.value) {
      fileInputRef.current.value = ''
    }
  }

  const triggerUpload = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    if (!fileInputRef || !fileInputRef.current) {
      return
    }
    removeFile()
    fileInputRef.current.click()
  }

  const proceedUpload = async (file: File) => {
    setIsUploading(true)
    try {
      const {
        data: {
          fileUploadUrl,
          fileUploadParameters,
          fileInfo: { fileId },
        },
      } = await uploadFileRequest({
        accountId,
        body: {
          fileName: file.name,
          fileSize: file.size,
          contentType: file.type,
          kind: fileKind,
        },
      }).unwrap()
      await uploadFile({ uploadUrl: fileUploadUrl, uploadParameters: fileUploadParameters, file }).unwrap()
      await updateFile({ accountId, fileId, body: { status: FileStatus.UPLOADED } }).unwrap()
      onFileUploaded(fileId)
    } catch (e) {
      removeFile()
    } finally {
      setIsUploading(false)
      removeFile()
    }
  }

  const handleFileUpload = async (evt: React.ChangeEvent<HTMLInputElement>) => {
    const file = evt.target.files ? evt.target.files[0] : null
    if (!file) {
      setErrorMessage('')
      return
    }

    const supportedTypes = supportedMimeTypes.split(',')
    if (!supportedTypes.includes(file.type)) {
      removeFile()
      setErrorMessage(`Unsupported file type. Only images are supported.`)
      return
    }

    if (file.size > maxFileSize) {
      removeFile()
      setErrorMessage(`File is too big. Maximum allowed size: ${humanFileSize(maxFileSize)}.`)
      return
    }

    if (fileType === 'image' && (imageMaxResolution || imageMinResolution)) {
      const img = new window.Image()
      const reader = new FileReader()
      reader.onload = (e) => {
        img.onload = () => {
          if (imageMaxResolution && (img.width > imageMaxResolution.maxWidth || img.height > imageMaxResolution.maxHeight)) {
            removeFile()
            setErrorMessage(
              `Image dimensions too large. Maximum allowed: ${imageMaxResolution.maxWidth}x${imageMaxResolution.maxHeight}px. Uploaded: ${img.width}x${img.height}px.`,
            )
            return
          }
          if (imageMinResolution && (img.width < imageMinResolution.minWidth || img.height < imageMinResolution.minHeight)) {
            removeFile()
            setErrorMessage(
              `Image dimensions too small. Minimum allowed: ${imageMinResolution.minWidth}x${imageMinResolution.minHeight}px. Uploaded: ${img.width}x${img.height}px.`,
            )
            return
          }
          proceedUpload(file)
        }
        img.onerror = () => {
          removeFile()
          setErrorMessage('Failed to read image dimensions.')
        }
        img.src = e.target?.result as string
      }
      reader.readAsDataURL(file)
      return
    } else {
      await proceedUpload(file)
    }
  }

  const resetFile = () => {
    removeFile()
    onFileReset()
    setIsUploading(false)
  }

  return (
    <>
      {logoFileUrl && (
        <FileDisplayModal
          fileUrl={logoFileUrl}
          show={showDisplayFileModal}
          onHide={() => {
            setShowDisplayFileModal(false)
          }}
          type={fileType}
        ></FileDisplayModal>
      )}
      <div
        className={classnames('p-2 rounded zocks-bg-color-gray-100 d-flex flex-row justify-content-between zocks-border-gray-200', {
          'zocks-border-color-danger': !!errorMessage,
        })}
      >
        <div className='d-flex gap-2'>
          <Button
            variant='dark'
            style={{ backgroundImage: `url(${buttonBackgroundImage})` }}
            className='p-0 square-button-medium button-custom-bg'
            onClick={handleDisplayFile}
            disabled={!logoFileUrl}
          >
            <i className='bi bi-search'></i>
          </Button>
          <div className='d-flex flex-column justify-content-center'>
            <div>
              <span className='bold'>{text}</span>
            </div>
            {fileInfo?.fileSize && <div>{humanFileSize(fileInfo.fileSize)}</div>}
          </div>
        </div>

        <Form.Control ref={fileInputRef} id={`${fileType}-file`} type='file' accept={supportedMimeTypes} onChange={handleFileUpload} hidden={true} />
        <ScopedBlock scope={scope} missingScopeAction={MissingScopeAction.DISABLE}>
          <Button
            disabled={!enabled || isLoading}
            variant='grayGhost'
            className={`zocks-border-gray-200 px-2 button-medium zocks-bg-color-white ${isLoading ? 'zocks-text-white' : 'zocks-text-gray-900'}`}
            onClick={triggerUpload}
          >
            <i className='bi bi-file-earmark-arrow-up'></i>
            <span>{buttonText}</span>
          </Button>
        </ScopedBlock>
      </div>
      <div className='mt-2'>
        <span className='text-danger'>{errorMessage}</span>
        {!!fileInfo && (
          <ScopedBlock scope={scope} missingScopeAction={MissingScopeAction.HIDE}>
            <Button onClick={resetFile} variant='link' className='text-decoration-underline button-link-small'>
              Restore to default
            </Button>
          </ScopedBlock>
        )}
      </div>
    </>
  )
}
